import React from 'react'
import { Box, Typography, Button, Stack, Alert, Paper, Container } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'

const STORAGE_KEY = 'squashpat_login_target'

type LoginLocationState = {
  from?: {
    pathname: string
    search?: string
    hash?: string
  }
}

const safeRedirect = (target: string) => {
  try {
    const url = new URL(target, window.location.origin)
    if (!target.startsWith('/') || url.origin !== window.location.origin) return '/admin/suggestions'
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '/admin/suggestions'
  }
}

const resolveRedirectPath = (state: LoginLocationState | null, search: string) => {
  const from = state?.from
  if (from?.pathname) return safeRedirect(`${from.pathname}${from.search ?? ''}${from.hash ?? ''}`)
  return safeRedirect(new URLSearchParams(search).get('next') || '/admin/suggestions')
}

const Login: React.FC = () => {
  const auth = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [error, setError] = React.useState<string | null>(null)
  const [submitting, setSubmitting] = React.useState(false)

  const [identifier, setIdentifier] = React.useState('')
  const [password, setPassword] = React.useState('')
  const identifierId = React.useId()
  const passwordId = React.useId()

  const redirectAfterLogin = React.useMemo(
    () => resolveRedirectPath(location.state as LoginLocationState | null, location.search),
    [location.state, location.search]
  )

  React.useEffect(() => {
    // Redirect if user is already logged in
    if (auth.initialized && auth.user) {
      let target = redirectAfterLogin
      try {
        const stored = sessionStorage.getItem(STORAGE_KEY)
        if (stored) {
          sessionStorage.removeItem(STORAGE_KEY)
          target = safeRedirect(stored)
        }
      } catch {
        // ignore
      }
      navigate(target || '/', { replace: true })
    }
  }, [auth.initialized, auth.user, navigate, redirectAfterLogin])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (submitting || !auth.initialized || !identifier || !password) return
    setSubmitting(true)
    setError(null)
    try {
      try {
        sessionStorage.setItem(STORAGE_KEY, redirectAfterLogin)
      } catch {
        // ignore
      }
      const success = await auth.login(identifier, password)
      if (!success) setError('Login fehlgeschlagen')
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      setError(message || 'Login failed')
    } finally {
      setSubmitting(false)
    }
  }

  const busy = submitting || !auth.initialized
  const combinedError = error || auth.error

  return (
    <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Container maxWidth="sm">
        <Paper elevation={6} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }} component="form" onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="h4" component="h1" fontWeight={700}>
                Login
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Bitte logge dich ein, um fortzufahren.
              </Typography>
            </Stack>

            {combinedError && <Alert severity="error">{combinedError}</Alert>}

            <Stack spacing={2}>
              <Box>
                <Typography component="label" htmlFor={identifierId} variant="body2" fontWeight={600} gutterBottom>Benutzername oder E-Mail</Typography>
                <Box
                  component="input"
                  id={identifierId}
                  autoComplete="username"
                  type="text"
                  required
                  value={identifier}
                  onChange={(e: any) => setIdentifier(e.target.value)}
                  disabled={busy}
                  sx={{
                    width: '100%',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    fontSize: '1rem',
                  }}
                />
              </Box>
              <Box>
                <Typography component="label" htmlFor={passwordId} variant="body2" fontWeight={600} gutterBottom>Passwort</Typography>
                <Box
                  component="input"
                  id={passwordId}
                  autoComplete="current-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e: any) => setPassword(e.target.value)}
                  disabled={busy}
                  sx={{
                    width: '100%',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    fontSize: '1rem',
                  }}
                />
              </Box>
            </Stack>

            <Button type="submit" size="large" variant="contained" disabled={busy}>
              {busy ? 'Anmelden…' : 'Anmelden'}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  )
}

export default Login
